import { useLayoutEffect, useEffect, useRef, type RefObject } from 'react';
import { useParams, useLocation, useSearchParams } from 'react-router-dom';
import {
  mergeRwPageSession,
  loadRwPageSession,
  type RegulatoryWatchPageSessionV1,
  type RegulatoryWatchSessionPageId,
} from './regulatoryWatchPageSession';

/**
 * Persists URL search params per Regulatory Watch list page. Restoration happens in
 * route loaders (`regulatoryWatchListRouteLoader`) before the first render.
 */
export function useRegulatoryWatchSearchSession(
  pageId: RegulatoryWatchSessionPageId
): void {
  const { workspaceUname } = useParams<{ workspaceUname: string }>();
  const [searchParams] = useSearchParams();
  const baselineSearchRef = useRef<string | null>(null);
  const identityRef = useRef<string>('');

  useEffect(() => {
    const identity = `${workspaceUname ?? ''}:${pageId}`;
    if (identityRef.current !== identity) {
      identityRef.current = identity;
      baselineSearchRef.current = null;
    }

    if (!workspaceUname) return;

    const current = searchParams.toString();
    if (baselineSearchRef.current === null) {
      baselineSearchRef.current = current;
      return;
    }
    if (current !== baselineSearchRef.current) {
      mergeRwPageSession(workspaceUname, pageId, {
        dirty: true,
        search: current,
      });
      baselineSearchRef.current = current;
    }
  }, [searchParams, workspaceUname, pageId]);
}

export function useRegulatoryWatchScrollSession(
  pageId: RegulatoryWatchSessionPageId,
  scrollRef: RefObject<HTMLElement | null>,
  restoreWhen: boolean
): void {
  const { workspaceUname } = useParams<{ workspaceUname: string }>();
  const location = useLocation();
  const restoredRef = useRef(false);

  useLayoutEffect(() => {
    restoredRef.current = false;
  }, [workspaceUname, pageId, location.pathname]);

  useLayoutEffect(() => {
    if (
      !workspaceUname ||
      !restoreWhen ||
      !scrollRef.current ||
      restoredRef.current
    ) {
      return;
    }
    const sess = loadRwPageSession(workspaceUname, pageId);
    const y = sess?.scrollY;
    if (typeof y === 'number' && y > 0) {
      scrollRef.current.scrollTop = y;
    }
    restoredRef.current = true;
  }, [workspaceUname, pageId, restoreWhen, scrollRef, location.pathname]);

  useEffect(() => {
    if (!workspaceUname || !restoreWhen) return;
    const el = scrollRef.current;
    if (!el) return;
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        mergeRwPageSession(workspaceUname, pageId, { scrollY: el.scrollTop });
      });
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      el.removeEventListener('scroll', onScroll);
    };
  }, [workspaceUname, pageId, scrollRef, restoreWhen]);
}

/**
 * Persists a value to the RW page session, skipping the initial render
 * (to avoid writing back the just-restored value).
 *
 * `toPatch` converts the current value into the session patch to merge.
 */
export function usePersistToRwSession<T>(
  pageId: RegulatoryWatchSessionPageId,
  value: T,
  toPatch: (v: T) => Partial<Omit<RegulatoryWatchPageSessionV1, 'v'>>,
  deps: readonly unknown[] = []
): void {
  const { workspaceUname } = useParams<{ workspaceUname: string }>();
  const skipRef = useRef(true);

  useEffect(() => {
    if (!workspaceUname) return;
    if (skipRef.current) {
      skipRef.current = false;
      return;
    }
    mergeRwPageSession(workspaceUname, pageId, {
      dirty: true,
      ...toPatch(value),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceUname, pageId, value, ...deps]);
}
